import React from "react";
import { useApp } from "./store.jsx";
import { requirementPhrase } from "./referral.jsx";
import { PrizeChoice } from "./prize-choice.jsx";

const { Button, IconButton, Icon, Badge, Card, FluteDivider, Wordmark } =
  window.CardboardmarketDesignSystem_2a3239;

/**
 * Finding out whether you won.
 *
 * A MOMENT, AND WORTH EXACTLY ONCE. Cards shuffle, flip one at a time, and then
 * either confetti or "not this time". What makes that a moment rather than a
 * nag is `campaign_reveal` — one row per person per draw, written when it has
 * played — so the banner offering it goes away and the overlay does not return
 * on the next page load.
 *
 * THE CARDS ARE THEATRE AND THE RESULT IS NOT. Winners were drawn server-side
 * from a stored seed, hours or days before anybody opens this; nothing here
 * decides anything. The flips are a way of being told, and the one that turns
 * over last is simply the last one — which is why the winning card is always in
 * that position rather than at a random index. Pretending the animation picks
 * would be a lie about a draw whose whole design is that it can be re-checked.
 */

const reduceMotion = () =>
  typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/**
 * Paper confetti — trimmed cards and strips in the brand palette.
 *
 * The design's shapes and palette, its gravity, and its opening burst from the
 * winning card. What has changed since is the OWNER'S, twice: it runs for a
 * duration rather than a fixed number of bangs, and every piece falls at its
 * own speed with its own sway so the screen reads as weather instead of a
 * sheet of paper coming down.
 *
 * Skipped entirely under reduced motion rather than shortened, which is the
 * rule the rest of this product already follows.
 */
function Confetti({ fire, origin }) {
  const ref = React.useRef(null);
  React.useEffect(() => {
    if (!fire || reduceMotion()) return undefined;
    const cv = ref.current;
    if (!cv) return undefined;
    const ctx = cv.getContext('2d');
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const size = () => {
      cv.width = window.innerWidth * dpr;
      cv.height = window.innerHeight * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    size();
    window.addEventListener('resize', size);

    const css = getComputedStyle(document.documentElement);
    const tok = (n, f) => (css.getPropertyValue(n).trim() || f);
    const colors = [
      tok('--orange-500', '#E4572E'), tok('--orange-500', '#E4572E'), tok('--orange-300', '#F2936F'),
      tok('--kraft-50', '#FAF7F1'), tok('--kraft-300', '#D8CBB4'),
      tok('--status-caution', '#F2B705'), tok('--status-verified', '#0E7C66'),
    ];

    const W = window.innerWidth, H = window.innerHeight;
    const parts = [];

    /**
     * One piece of paper.
     *
     * EVERY NUMBER HERE VARIES PER PIECE, which is the whole of the owner's
     * note: "instead of making all the confetti fall at the same speed and
     * position, we should stagger each one a bit and keep the confetti falling
     * like snow." Paper that shares a terminal velocity falls as a sheet, and a
     * sheet reads as one object rather than a hundred.
     *
     * `fall` is that terminal velocity, and it is the important one — a piece
     * with 1.4 drifts down while its neighbour at 3.0 drops past it, which is
     * what makes a crowd of them look like weather. `swayBy` and `swaySpeed`
     * are the sideways wander, also per piece, so nothing sways in step.
     */
    const add = (x, y, vx, vy, opts = {}) => {
      parts.push({
        x, y, vx, vy,
        r: Math.random() * Math.PI * 2,
        vr: (Math.random() - 0.5) * 0.3,
        flip: Math.random() * Math.PI,
        vf: 0.05 + Math.random() * 0.12,
        sway: Math.random() * Math.PI * 2,
        swayBy: 0.35 + Math.random() * 1.1,
        swaySpeed: 0.02 + Math.random() * 0.05,
        fall: opts.fall ?? (1.3 + Math.random() * 1.9),
        drag: 0.978 + Math.random() * 0.016,
        shape: Math.random() < 0.55 ? 'card' : 'strip',
        w: 7 + Math.random() * 5,
        h: 10 + Math.random() * 6,
        c: colors[(Math.random() * colors.length) | 0],
      });
    };

    /** A cannon: everything leaves from one point, fast, in a spread. */
    const burst = (x, y, n, spread, power, up) => {
      for (let i = 0; i < n; i += 1) {
        const a = up + (Math.random() - 0.5) * spread;
        const v = power * (0.55 + Math.random() * 0.6);
        add(x, y, Math.cos(a) * v, Math.sin(a) * v);
      }
    };

    /**
     * Snow: a few pieces entering from above, anywhere across the width.
     *
     * STAGGERED BY STARTING ABOVE THE SCREEN AT DIFFERENT HEIGHTS rather than by
     * a timer. A timer would drop them in rows however small the interval; a
     * random head start means they are already unaligned the moment they exist.
     * They enter slowly — the fall speed does the rest.
     */
    const snow = (n) => {
      for (let i = 0; i < n; i += 1) {
        add(Math.random() * W, -20 - Math.random() * 160, (Math.random() - 0.5) * 0.8, Math.random() * 0.6, {
          // Slower than a burst's debris: this is the stuff still coming down
          // long after the bang, and it is what keeps the screen alive.
          fall: 0.9 + Math.random() * 1.3,
        });
      }
    };

    /*
     * EIGHT SECONDS OF IT, at the owner's ask — it was a fixed list of six
     * bursts. A duration is the better knob: a count says how many bangs, a
     * duration says how long the room stays loud, which is the thing anybody
     * actually has an opinion about. Tuning it is one number rather than a
     * table to re-balance.
     */
    const DURATION = 8000;
    const EVERY = 620;
    /*
     * AND A CEILING, which a duration needs and a fixed count did not. Six
     * bursts could not run away; "keep going for eight seconds" can — a slow
     * phone that drops frames also clears particles more slowly, so the live
     * set grows exactly where it can least afford to. Past this a wave is
     * SKIPPED rather than queued: the screen is already full, and nobody can
     * tell which burst they are looking at.
     */
    const CEILING = 900;

    // The opening, from the winning card. That is the instant of finding out;
    // everything after it is the room carrying on.
    const ox = origin ? origin.x : W / 2, oy = origin ? origin.y : H * 0.4;
    burst(ox, oy, 140, Math.PI * 2, 13, -Math.PI / 2);
    snow(40);

    let wave = 0;
    const keepGoing = setInterval(() => {
      wave += 1;
      if (parts.length > CEILING) return;
      // Left, right, and snow throughout rather than as its own turn — the
      // owner asked for it KEPT, and something always coming down is what
      // stops eight seconds of corner bursts reading as a loop.
      snow(14);
      if (wave % 2) burst(-10, H * (0.6 + Math.random() * 0.3), 70, 0.7, 20, -Math.PI / 3);
      else burst(W + 10, H * (0.6 + Math.random() * 0.3), 70, 0.7, 20, -Math.PI * 2 / 3);
    }, EVERY);
    const stopLaunching = setTimeout(() => clearInterval(keepGoing), DURATION);

    const draw = (p) => {
      ctx.save();
      ctx.translate(p.x, p.y);
      ctx.rotate(p.r);
      ctx.scale(1, Math.cos(p.flip));
      ctx.fillStyle = p.c;
      if (p.shape === 'card') {
        const w = p.w, h = p.h, cut = w * 0.32;
        ctx.beginPath();
        ctx.moveTo(-w / 2, -h / 2); ctx.lineTo(w / 2 - cut, -h / 2); ctx.lineTo(w / 2, -h / 2 + cut);
        ctx.lineTo(w / 2, h / 2); ctx.lineTo(-w / 2, h / 2);
        ctx.closePath(); ctx.fill();
      } else {
        ctx.fillRect(-1.5, -p.h / 2, 3, p.h);
      }
      ctx.restore();
    };

    let raf, last = performance.now();
    const start = performance.now();
    const tick = (now) => {
      const dt = Math.min(2, (now - last) / 16.67);
      last = now;
      ctx.clearRect(0, 0, W + 40, H + 40);
      for (let i = parts.length - 1; i >= 0; i -= 1) {
        const p = parts[i];
        p.vy += 0.32 * dt;
        p.vx *= Math.pow(p.drag, dt);
        p.vy *= Math.pow(p.drag, dt);
        p.sway += p.swaySpeed * dt;
        p.x += (p.vx + Math.sin(p.sway) * p.swayBy) * dt;
        p.y += p.vy * dt;
        p.r += p.vr * dt;
        p.flip += p.vf * dt;
        /*
         * ITS OWN terminal velocity, not one shared number.
         *
         * This was `if (p.vy > 3.2) p.vy = 3.2 + …` for every piece, so
         * everything settled into the same descent and fell as a sheet. Per
         * piece, the slow ones hang while the fast ones drop past them, which
         * is what reads as snow rather than a curtain.
         */
        if (p.vy > p.fall) p.vy = p.fall + (p.vy - p.fall) * 0.9;
        if (p.y > H + 30) parts.splice(i, 1); else draw(p);
      }
      /*
       * Runs while anything is still falling, and never stops before the last
       * wave is launched. A floor shorter than DURATION would create bursts
       * into a canvas nothing was painting — which is what a fixed 1400ms did
       * the first time this grew.
       */
      if (parts.length || now - start < DURATION) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);

    return () => {
      cancelAnimationFrame(raf);
      clearInterval(keepGoing);
      clearTimeout(stopLaunching);
      window.removeEventListener('resize', size);
    };
  }, [fire, origin]);
  return <canvas ref={ref} className="rv-confetti" aria-hidden="true" />;
}

function EntryCard({ n, state, winner, refEl, t }) {
  return (
    <div className="rv-card" data-state={state} data-win={winner ? '1' : '0'} ref={refEl}>
      <div className="rv-card__in">
        <div className="rv-card__back">
          <span className="rv-card__tile" />
          <span className="rv-card__n">{t("Entry {n}", { n: String(n).padStart(2, '0') })}</span>
        </div>
        <div className="rv-card__face">
          <span className="rv-card__n">{t("Entry {n}", { n: String(n).padStart(2, '0') })}</span>
          <span className="rv-card__word">{winner ? t("drawn") : t("not drawn")}</span>
          {winner ? <Icon name="star" size={20} /> : <span />}
        </div>
      </div>
    </div>
  );
}

export function RevealOverlay() {
  const { revealId, closeReveal, settleResult, campaigns, liveCampaign, go, t, locale } = useApp();
  const c = campaigns.find((x) => x.id === revealId);
  const [phase, setPhase] = React.useState('shuffle');
  const [flipped, setFlipped] = React.useState(0);
  const [origin, setOrigin] = React.useState(null);
  const winRef = React.useRef(null);

  const won = Boolean(c && c.youWon);
  // Five at most: beyond that they stop being readable and the wait stops being
  // a moment. Somebody with forty entries watches five flip, which is honest —
  // the text beside them says how many were really in.
  const count = c ? Math.max(1, Math.min(c.myEntries || 0, 5)) : 0;
  const winIndex = count - 1;

  React.useEffect(() => {
    if (!c) return undefined;
    setPhase('shuffle'); setFlipped(0); setOrigin(null);
    document.body.style.overflow = 'hidden';

    /*
     * Under reduced motion every step is clamped to 300ms rather than removed.
     * The sequence still happens — it is how the result is delivered — but it
     * does not perform.
     */
    const quick = reduceMotion();
    const timers = [];
    const at = (ms, fn) => timers.push(setTimeout(fn, quick ? Math.min(ms, 300) : ms));

    at(1500, () => setPhase('flip'));
    for (let i = 0; i < count; i += 1) at(1700 + i * 750, () => setFlipped(i + 1));
    const done = 1700 + (count - 1) * 750;
    if (won) {
      at(done + 380, () => {
        const r = winRef.current && winRef.current.getBoundingClientRect();
        setOrigin(r ? { x: r.left + r.width / 2, y: r.top + r.height / 2 } : null);
        setPhase('won');
      });
    } else {
      at(done + 900, () => setPhase('lost'));
    }
    /*
     * Recorded as watched when it RESOLVES, not when it is closed. Somebody who
     * shuts the tab on the confetti has still been told, and offering them the
     * same reveal tomorrow would be the product pretending it never happened.
     */
    at(done + 1000, () => settleResult(c.id));

    return () => {
      timers.forEach(clearTimeout);
      document.body.style.overflow = '';
    };
  // The draw being revealed, not the object: `campaigns` is refetched and the
  // row is replaced, which would restart the whole sequence mid-flip.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [revealId]);

  if (!c) return null;

  const resolved = phase === 'won' || phase === 'lost';
  const shop = c.handoff && c.handoff.type === 'shop';
  const day = (iso) => new Date(iso).toLocaleDateString(locale, { day: 'numeric', month: 'long' });
  const next = liveCampaign && liveCampaign.id !== c.id
    ? liveCampaign
    : campaigns.filter((x) => x.state === 'scheduled')
        .slice().sort((a, b) => new Date(a.startsAt) - new Date(b.startsAt))[0];

  const status = phase === 'shuffle'
    ? t("Shuffling {n} entries", { n: c.entries })
    : phase === 'flip'
      // Most people have one. A separate key for it, like everywhere else here.
      ? (c.myEntries === 1 ? t("Checking your 1 entry") : t("Checking your {n} entries", { n: c.myEntries }))
      : null;

  return (
    <div className="rv" data-phase={phase} role="dialog" aria-modal="true" aria-label={c.title}>
      <FluteDivider tone="brand" />
      <div className="rv__bar">
        <Wordmark size={16} tone="inverse" />
        {/* Not closable until it has resolved — half a reveal is not a result,
            and a person who dismissed it would be owed it again. */}
        <IconButton icon="x" label={t("Close")} variant="ghost" onClick={closeReveal}
                    disabled={!resolved} style={{ color: 'var(--kraft-50)' }} />
      </div>
      <div className="rv__stage">
        <span className="rv__meta">{c.title} · {t("drawn {day}", { day: day(c.drawAt) })}</span>
        <div className="rv__cards" data-count={count}>
          {Array.from({ length: count }, (_, i) => (
            <EntryCard key={i} n={i + 1} t={t} winner={won && i === winIndex}
                       state={flipped > i ? 'up' : 'down'}
                       refEl={won && i === winIndex ? winRef : null} />
          ))}
        </div>
        <div className="rv__status" aria-live="polite">
          {status ? <><span>{status}</span><span className="rv__dots"><i /><i /><i /></span></> : null}
        </div>

        {phase === 'won' ? (
          <div className="rv__result">
            <h1 className="rv__head">{t("you won a {prize}", { prize: c.prize.toLowerCase() })}</h1>
            <p className="rv__body">
              {t("One of {n} drawn from {total} entries.", { n: c.winners, total: c.entries })}
            </p>
            <Card cut padding="var(--space-6)"
                  style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)', boxShadow: 'var(--shadow-hard)', width: '100%', maxWidth: 440, textAlign: 'left' }}>
              {/*
                NO ADDRESS FORM, which the design had. The owner collects
                addresses himself — "I manually collect winner's addresses and
                ship the prize to them" — so a button promising to save one here
                would describe machinery this product does not have. The same
                goes for the shop's name, its distance and a pickup deadline:
                `handoff` stores a TYPE, not a place.
              */}
              {/* And which one, in the moment they find out — the same control
                  the draw's page carries, so there is one way to choose. */}
              <PrizeChoice campaign={c} />
              {shop && c.youWon.pickupCode ? (
                <>
                  <div className="cbm-pickup">
                    <span className="cbm-label">{t("Pickup code")}</span>
                    <span className="cbm-pickup__code">{c.youWon.pickupCode}</span>
                  </div>
                  <span className="cbm-meta">{t("Show this code when you collect it.")}</span>
                </>
              ) : (
                <span style={{ font: 'var(--type-body-sm)', color: 'var(--text-muted)' }}>
                  {t("We will message you to arrange it.")}
                </span>
              )}
            </Card>
            <Button variant="ghost" onClick={closeReveal} style={{ color: 'var(--kraft-200)' }}>{t("Done")}</Button>
          </div>
        ) : null}

        {phase === 'lost' ? (
          <div className="rv__result">
            <h1 className="rv__head">{t("not this time")}</h1>
            <p className="rv__body">
              {/* One placeholder per thing. The first draft used {n} twice, for
                  the winner count and again for itself — which reads fine in
                  English and is unanswerable for a translator. */}
              {t("{winners} winners were drawn from {total} entries, and yours were not among them. Thanks for bringing people in — they are still here trading.",
                 { winners: c.winners, total: c.entries })}
            </p>
            {next ? (
              <Card cut padding="var(--space-6)"
                    style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)', width: '100%', maxWidth: 440, textAlign: 'left' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                  {next.state === 'live'
                    ? <Badge tone="brand">{t("Open")}</Badge>
                    : <Badge tone="neutral">{t("Starts {day}", { day: day(next.startsAt) })}</Badge>}
                </div>
                <span style={{ font: 'var(--type-h3)', color: 'var(--text-strong)' }}>{next.title}</span>
                <span style={{ font: 'var(--type-body-sm)', color: 'var(--text-muted)' }}>
                  {next.state === 'live'
                    ? t("Every friend who joins and does this adds an entry: {what}.", { what: requirementPhrase(next, t) })
                    : t("Your link keeps working — friends who join once it opens count.")}
                </span>
                {/*
                  No "remind me": there is no notification for a draw opening,
                  and a button that sets a reminder nothing sends is worse than
                  no button. The invite screen is where this actually continues.
                */}
                <div style={{ display: 'flex', gap: 'var(--space-3)', flexWrap: 'wrap' }}>
                  <Button iconLeft="user" onClick={() => { closeReveal(); go('invite'); }}>{t("Invite friends")}</Button>
                  <Button variant="outline" onClick={() => { closeReveal(); go('campaign', { id: next.id }); }}>
                    {t("See the draw")}
                  </Button>
                </div>
              </Card>
            ) : null}
            <Button variant="ghost" onClick={closeReveal} style={{ color: 'var(--kraft-200)' }}>{t("Close")}</Button>
          </div>
        ) : null}
      </div>
      <Confetti fire={phase === 'won'} origin={origin} />
    </div>
  );
}

/**
 * The draw is done and you have not looked yet.
 *
 * Deliberately says nothing about the outcome — not a hint in the wording, not
 * a colour. The whole point of the overlay is that the person finds out there,
 * and a banner that reads differently for a winner would give it away before
 * they pressed anything.
 */
export function DrawBanner({ campaign, compact }) {
  const { checkResult, t, locale } = useApp();
  const day = new Date(campaign.drawAt).toLocaleDateString(locale, { day: 'numeric', month: 'long' });
  return (
    <div className="rv-banner" data-compact={compact ? '1' : '0'}>
      <FluteDivider tone="brand" />
      <div className="rv-banner__in">
        <div style={{ flex: 1, minWidth: 200 }}>
          <span className="rv-banner__meta">{t("The draw is done")} · {t("drawn {day}", { day })}</span>
          {/*
            THE CAMPAIGN'S NAME IS THE HEADLINE, not "The draw is done".
            Reported: "it doesn't show anywhere which campaign it is." Which is
            fair — draws can overlap, so a banner that only says one finished is
            a banner about an unnamed thing. The state moves up to the meta line
            where the date already is, and the title takes the place it should
            have had.
          */}
          <div className="rv-banner__title">{campaign.title}</div>
          <div className="rv-banner__body">
            {campaign.myEntries === 1
              ? t("1 of the {total} entries was yours. {winners} winners to draw.",
                  { total: campaign.entries, winners: campaign.winners })
              : t("{n} of the {total} entries were yours. {winners} winners to draw.",
                  { n: campaign.myEntries, total: campaign.entries, winners: campaign.winners })}
          </div>
        </div>
        <Button size={compact ? 'md' : 'lg'} iconLeft="star" onClick={() => checkResult(campaign.id)}>
          {t("Check if I won")}
        </Button>
      </div>
    </div>
  );
}
